import { useMemo, useState } from 'react'
import { useTemplate } from '@/contexts/TemplateContext'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface DateSelectionProps {
    selectedDate: string | null
    onDateChange: (date: string) => void
    minDate?: Date
    maxDate?: Date
}

const DAYS_TO_SHOW = 14

function startOfDay(date: Date): Date {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
}

function addDays(date: Date, days: number): Date {
    const d = new Date(date)
    d.setDate(d.getDate() + days)
    return d
}

function isSameDay(date1: Date, date2: Date): boolean {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    )
}

function formatDate(date: Date) {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })
    const dayNumber = date.getDate().toString()
    const monthName = date.toLocaleDateString('en-US', { month: 'short' })
    return { dayName, dayNumber, monthName }
}

function formatDateISO(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function DateSelection({
    selectedDate,
    onDateChange,
    minDate,
    maxDate,
}: DateSelectionProps) {
    const { colors } = useTemplate()
    const [offset, setOffset] = useState(0)

    const today = useMemo(() => startOfDay(new Date()), [])

    const startDate = useMemo(() => {
        const base = minDate ? startOfDay(minDate) : today
        return addDays(base, offset * DAYS_TO_SHOW)
    }, [offset, minDate, today])

    const dates = useMemo(() => {
        const result: Date[] = []
        const effectiveMax = maxDate ? startOfDay(maxDate) : null
        let current = new Date(startDate)

        for (let i = 0; i < DAYS_TO_SHOW; i++) {
            if (effectiveMax && current > effectiveMax) break
            result.push(new Date(current))
            current.setDate(current.getDate() + 1)
        }
        return result
    }, [startDate, maxDate])

    const handlePrev = () => {
        if (offset > 0) setOffset((o) => o - 1)
    }

    const handleNext = () => {
        const lastDate = dates[dates.length - 1]
        const effectiveMax = maxDate ? startOfDay(maxDate) : null
        if (!effectiveMax || lastDate < effectiveMax) {
            setOffset((o) => o + 1)
        }
    }

    const canGoPrev = offset > 0
    const canGoNext = maxDate
        ? dates[dates.length - 1] < startOfDay(maxDate)
        : true

    const monthYearLabel = startDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={handlePrev}
                    disabled={!canGoPrev}
                    className="p-2 rounded-full hover:bg-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous dates"
                >
                    <ChevronLeft
                        className="w-5 h-5 text-foreground"
                        style={{ color: colors.colors.text }}
                    />
                </button>

                <span
                    className="text-sm font-medium text-foreground"
                    style={{ color: colors.colors.text }}
                >
                    {monthYearLabel}
                </span>

                <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canGoNext}
                    className="p-2 rounded-full hover:bg-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next dates"
                >
                    <ChevronRight
                        className="w-5 h-5 text-foreground"
                        style={{ color: colors.colors.text }}
                    />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-2">
                {dates.map((date) => {
                    const { dayName, dayNumber, monthName } = formatDate(date)
                    const dateISO = formatDateISO(date)
                    const isSelected = selectedDate === dateISO
                    const isToday = isSameDay(date, today)
                    const isPast = date < today && !isToday

                    return (
                        <button
                            key={dateISO}
                            type="button"
                            onClick={() => onDateChange(dateISO)}
                            disabled={isPast}
                            className={`
                                flex flex-col items-center justify-center p-2 rounded-lg
                                aspect-square min-h-[60px]
                                transition-colors duration-200
                                ${
                                    isSelected
                                        ? 'text-white'
                                        : isPast
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:opacity-80'
                                }
                                ${
                                    isToday && !isSelected
                                        ? 'border-2'
                                        : ''
                                }
                            `}
                            style={
                                isSelected
                                    ? { backgroundColor: colors.colors.primary }
                                    : {}
                            }
                            aria-label={date.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            })}
                            aria-pressed={isSelected}
                        >
                            <span
                                className={`text-xs ${
                                    isSelected
                                        ? 'text-white/80'
                                        : 'text-foreground'
                                }`}
                                style={
                                    !isSelected
                                        ? { color: colors.colors.text }
                                        : {}
                                }
                            >
                                {dayName}
                            </span>
                            <span
                                className={`text-lg font-semibold ${
                                    isSelected
                                        ? 'text-white'
                                        : 'text-foreground'
                                }`}
                                style={
                                    !isSelected
                                        ? { color: colors.colors.text }
                                        : {}
                                }
                            >
                                {dayNumber}
                            </span>
                            <span
                                className={`text-xs text-muted-foreground ${
                                    isSelected ? 'text-white/70' : ''
                                }`}
                            >
                                {monthName}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
